import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FiCopy } from 'react-icons/fi';
import { Footer, MarketingHeader, ProductFactBlurb, SEO } from '@/components';
import { track } from '@/lib/analytics';
import { COURT_ORDER_TEMPLATES } from '@/lib/court-order-templates';

const nunitoFont = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

function scrollToTemplate(anchorId: string) {
  const el = document.getElementById(anchorId);
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function handleCopyTemplate(templateId: string, body: string) {
  track('Copy Court Order Template', { templateId });
  navigator.clipboard.writeText(body).catch((err) => {
    console.error('Failed to copy text:', err);
  });
}

function TableOfContentsSidebar() {
  return (
    <nav
      className='hidden lg:block w-56 shrink-0'
      aria-label='Template sections'
    >
      <div className='sticky top-28 pr-2'>
        <p
          className='text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3'
          style={{ ...nunitoFont, fontWeight: 600 }}
        >
          On this page
        </p>
        <ul className='space-y-2 border-l border-gray-200 pl-3'>
          {COURT_ORDER_TEMPLATES.map((t, index) => (
            <li key={t.id}>
              <button
                type='button'
                onClick={() => scrollToTemplate(`template-${t.id}`)}
                className='text-left text-sm text-primary hover:text-primary-hover hover:underline w-full'
                style={{ ...nunitoFont, fontWeight: 400 }}
              >
                {index + 1}. {t.title}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

function TableOfContentsMobile() {
  return (
    <div className='lg:hidden mb-8 -mx-1'>
      <p
        className='text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 px-1'
        style={{ ...nunitoFont, fontWeight: 600 }}
      >
        Jump to section
      </p>
      <div className='w-full overflow-x-auto scrollbar-hide pb-1'>
        <div className='flex gap-2 min-w-max px-1'>
          {COURT_ORDER_TEMPLATES.map((t, index) => (
            <button
              key={t.id}
              type='button'
              onClick={() => scrollToTemplate(`template-${t.id}`)}
              className='whitespace-nowrap rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm hover:border-primary hover:text-primary'
              style={{ ...nunitoFont, fontWeight: 500 }}
            >
              {index + 1}. {t.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CourtOrderLanguagePage() {
  useEffect(() => {
    track('Court Order Language Screen');
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Court Order Language for Co-Parenting Apps | Harbor'
        description='Copy-paste court order and parenting plan language for mandating or recommending the Harbor co-parenting platform. Templates for communication, expenses, custody calendar, professional access, and document vault.'
        path='/court-order-language'
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-10'>
        <div className='max-w-4xl mx-auto px-6 text-center'>
          <h1
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{ ...nunitoFont, fontWeight: 700 }}
          >
            Court Order Language Templates
          </h1>
          <p
            className='text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed'
            style={{ ...nunitoFont, fontWeight: 400 }}
          >
            Starting points for attorneys drafting custody orders, parenting
            plans, and stipulated agreements. Each section is self-contained and
            can be pasted into your filing; review and adapt for your
            jurisdiction and facts.
          </p>
          <ProductFactBlurb />
        </div>
      </section>

      <div className='max-w-7xl mx-auto px-6 py-12 lg:py-16'>
        <div className='flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12'>
          <TableOfContentsSidebar />

          <div className='flex-1 min-w-0'>
            <TableOfContentsMobile />

            <div className='space-y-16'>
              {COURT_ORDER_TEMPLATES.map((template, index) => (
                <section
                  key={template.id}
                  id={`template-${template.id}`}
                  className='scroll-mt-28'
                >
                  <h2
                    className='text-2xl md:text-3xl font-bold text-gray-900 mb-4'
                    style={{ ...nunitoFont, fontWeight: 700 }}
                  >
                    {index + 1}. {template.title}
                  </h2>

                  <div className='mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5'>
                    <p
                      className='text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2'
                      style={{ ...nunitoFont, fontWeight: 600 }}
                    >
                      Attorney note (not part of the order)
                    </p>
                    <p
                      className='text-gray-700 leading-relaxed italic'
                      style={{ ...nunitoFont, fontWeight: 400 }}
                    >
                      {template.attorneyNote}
                    </p>
                  </div>

                  <div className='mb-3 flex flex-wrap items-center gap-3'>
                    <button
                      type='button'
                      onClick={() =>
                        handleCopyTemplate(template.id, template.body)
                      }
                      className='inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-white font-semibold hover:bg-primary-hover transition-colors'
                      style={{ ...nunitoFont, fontWeight: 600 }}
                    >
                      <FiCopy className='h-5 w-5' aria-hidden />
                      Copy to clipboard
                    </button>
                    <span
                      className='text-sm text-gray-500'
                      style={{ ...nunitoFont, fontWeight: 400 }}
                    >
                      Copies order text only (excludes attorney note above).
                    </span>
                  </div>

                  <pre
                    className='overflow-x-auto rounded-lg border border-gray-200 bg-white p-4 md:p-6 text-sm leading-relaxed text-gray-900 shadow-sm whitespace-pre-wrap break-words'
                    style={{
                      fontFamily:
                        '"Courier New", Courier, "Liberation Mono", monospace',
                    }}
                  >
                    {template.body}
                  </pre>
                </section>
              ))}
            </div>

            <div
              className='mt-16 rounded-lg border border-amber-200 bg-amber-50 p-6 md:p-8'
              role='note'
            >
              <p
                className='text-gray-800 leading-relaxed'
                style={{ ...nunitoFont, fontWeight: 400 }}
              >
                <strong className='font-semibold'>Disclaimer.</strong> These
                templates are provided for informational purposes only and do not
                constitute legal advice. Attorneys should review and adapt all
                language to comply with applicable state law, local court rules,
                and the specific facts of each case.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/court-order-language')({
  component: CourtOrderLanguagePage,
});
